import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import NotFound from '@/components/NotFound';

// Built-in safety-net redirects (non-editable). Admin-managed redirects live in
// the UrlRedirect entity and take priority over these. Kept so a misconfigured or
// empty database never silently breaks known critical links.
const LEGACY_REDIRECTS = {
  '/simpowbrk': '/shop/6a7af261c7a6bafd94be5273',
  '/simucube-premium-stop-button-mount': '/shop/6a7af261b8edc8cd43b52d8e',
};

// Catch-all for old store links Google still indexes for products.
// 1. Admin-managed UrlRedirect (DB) — checked first, takes priority.
// 2. Built-in static legacy map.
// 3. Otherwise try to match the old slug to a current Product by permalink or
//    SKU and send the visitor straight to that product's page.
// 4. If nothing matches, show the branded 404 page.
export default function LegacyRedirect() {
  const { pathname } = useLocation();
  const [target, setTarget] = useState(null); // string = navigate; 'NOTFOUND' = show 404

  useEffect(() => {
    const normalized = pathname.toLowerCase();
    const slug = normalized.replace(/^\//, '').replace(/\/$/, '');

    (async () => {
      try {
        // 1. Admin-managed DB redirects (active only).
        const dbRedirects = await base44.entities.UrlRedirect.list('-updated_date', 500);
        const match = (dbRedirects || []).find(
          (r) => r.active !== false && r.source_path && r.source_path.toLowerCase() === normalized
        );
        if (match && match.destination) { setTarget(match.destination); return; }

        // 2. Built-in static map.
        const mapped = LEGACY_REDIRECTS[normalized];
        if (mapped) { setTarget(mapped); return; }

        // 3. Dynamic product match by permalink / SKU.
        if (slug) {
          let product = null;
          const byPermalink = await base44.entities.Product.filter({ permalink: slug });
          if (byPermalink && byPermalink.length > 0) product = byPermalink[0];
          if (!product) {
            const bySku = await base44.entities.Product.filter({ sku: slug });
            if (bySku && bySku.length > 0) product = bySku[0];
          }
          if (product && product.id) { setTarget(`/shop/${product.id}`); return; }
        }

        // 4. No match — branded 404.
        setTarget('NOTFOUND');
      } catch {
        // DB fetch failed: still honour the static map, else 404.
        const mapped = LEGACY_REDIRECTS[normalized];
        setTarget(mapped || 'NOTFOUND');
      }
    })();
  }, [pathname]);

  if (!target) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-white/10 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }
  if (target === 'NOTFOUND') return <NotFound />;
  return <Navigate to={target} replace />;
}